import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TermsOfService = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  return (
    <div className={`min-h-screen bg-gray-50 ${isAr ? 'rtl' : 'ltr'}`} data-testid="terms-page">
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-900">
            {isAr ? 'شروط الخدمة' : 'Terms of Service'}
          </h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 pb-32">
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
          <p className="text-sm text-gray-500">{isAr ? 'آخر تحديث: مارس 2026' : 'Last Updated: March 2026'}</p>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '1. قبول الشروط' : '1. Acceptance of Terms'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'باستخدامك لموقع Findadz.com، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على أي من هذه الشروط، يرجى عدم استخدام الموقع.'
                : 'By accessing and using Findadz.com, you agree to be bound by these Terms of Service. If you do not agree to any of these terms, please do not use the platform.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '2. وصف الخدمة' : '2. Description of Service'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'Findadz هو سوق إلكتروني للإعلانات المبوبة يعمل في الإمارات العربية المتحدة. نحن نوفر منصة للمستخدمين لنشر وتصفح الإعلانات لمختلف الفئات بما في ذلك العقارات والسيارات والوظائف والإلكترونيات والمزيد.'
                : 'Findadz is an online classifieds marketplace operating in the United Arab Emirates. We provide a platform for users to post and browse listings across various categories including property, motors, jobs, electronics, and more.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '3. حسابات المستخدمين' : '3. User Accounts'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'يجب أن يكون عمرك 18 عامًا على الأقل لإنشاء حساب. أنت مسؤول عن الحفاظ على سرية بيانات حسابك وعن جميع الأنشطة التي تتم تحت حسابك.'
                : 'You must be at least 18 years old to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '4. قواعد النشر' : '4. Listing Rules'}</h2>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'يجب أن تكون جميع الإعلانات دقيقة وصادقة' : 'All listings must be accurate and truthful'}</li>
              <li>{isAr ? 'يحظر نشر محتوى غير قانوني أو احتيالي أو مضلل' : 'Illegal, fraudulent, or misleading content is prohibited'}</li>
              <li>{isAr ? 'يجب عدم نشر عناصر محظورة بموجب قوانين الإمارات' : 'Items prohibited under UAE law must not be listed'}</li>
              <li>{isAr ? 'يحق لنا إزالة أي إعلان ينتهك هذه الشروط' : 'We reserve the right to remove any listing that violates these terms'}</li>
              <li>{isAr ? 'يجب أن تكون الصور أصلية وتمثل العنصر المعلن عنه' : 'Images must be original and represent the item being listed'}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '5. المحتوى المحظور' : '5. Prohibited Content'}</h2>
            <ul className="text-sm text-gray-600 leading-relaxed list-disc list-inside space-y-2">
              <li>{isAr ? 'الأسلحة والمواد الخطرة' : 'Weapons and hazardous materials'}</li>
              <li>{isAr ? 'المواد المقلدة أو المسروقة' : 'Counterfeit or stolen goods'}</li>
              <li>{isAr ? 'المخدرات والمواد الخاضعة للرقابة' : 'Drugs and controlled substances'}</li>
              <li>{isAr ? 'المحتوى الذي ينتهك حقوق الملكية الفكرية' : 'Content that infringes intellectual property rights'}</li>
              <li>{isAr ? 'أي عنصر يخالف قوانين الإمارات العربية المتحدة' : 'Any item that violates UAE federal or local laws'}</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '6. المعاملات' : '6. Transactions'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'Findadz هو منصة إعلانات مبوبة وليس طرفًا في أي معاملة بين المشترين والبائعين. نحن لا نضمن جودة أو سلامة أو قانونية العناصر المعلن عنها. جميع المعاملات تتم على مسؤوليتك الخاصة.'
                : 'Findadz is a classifieds platform and is not a party to any transaction between buyers and sellers. We do not guarantee the quality, safety, or legality of items listed. All transactions are conducted at your own risk.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '7. حدود المسؤولية' : '7. Limitation of Liability'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'لن تكون Findadz مسؤولة عن أي أضرار مباشرة أو غير مباشرة أو عرضية أو تبعية ناتجة عن استخدامك للمنصة أو عدم قدرتك على استخدامها.'
                : 'Findadz shall not be liable for any direct, indirect, incidental, or consequential damages arising from your use of, or inability to use, the platform.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '8. إنهاء الحساب' : '8. Account Termination'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'نحتفظ بالحق في تعليق أو إنهاء حسابك في أي وقت إذا انتهكت هذه الشروط أو شاركت في أنشطة احتيالية أو مسيئة.'
                : 'We reserve the right to suspend or terminate your account at any time if you violate these terms or engage in fraudulent or abusive activities.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '9. القانون الحاكم' : '9. Governing Law'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'تخضع هذه الشروط لقوانين دولة الإمارات العربية المتحدة وتفسر وفقًا لها. أي نزاعات تنشأ عن هذه الشروط تخضع للاختصاص الحصري لمحاكم الإمارات.'
                : 'These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the UAE courts.'}
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-900">{isAr ? '10. الاتصال بنا' : '10. Contact Us'}</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              {isAr
                ? 'إذا كانت لديك أي أسئلة حول هذه الشروط، يرجى الاتصال بنا على findadz@hotmail.com'
                : 'If you have any questions about these Terms, please contact us at findadz@hotmail.com'}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
